import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { SHERIFF_GOOGLE_PLAY_TIMELINE } from "sheriffs";
import { PostingInfo } from "api";
import { ClientState } from "state/state";
import { confirmBox } from "state/confirmbox/actions";
import {
    postingCommentAddedBlock,
    postingCommentAddedUnblock,
    postingCommentsSubscribe,
    postingCommentsUnsubscribe,
    postingCopyLink,
    postingDelete
} from "state/postings/actions";
import { postingReply } from "state/postingreply/actions";
import { storyDelete, storyPinningUpdate } from "state/stories/actions";
import { openChangeDateDialog } from "state/changedatedialog/actions";
import { openSourceDialog } from "state/sourcedialog/actions";
import { entryCopyText } from "state/entrycopytextdialog/actions";
import { getHomeOwnerName } from "state/home/selectors";
import { isPermitted } from "state/node/selectors";
import { getSetting } from "state/settings/selectors";
import {
    getPostingCommentAddedInstantBlockId,
    getPostingCommentsSubscriptionId,
    isPostingSheriff,
    isPostingSheriffProhibited
} from "state/postings/selectors";
import { commentsShowInvisibleSet } from "state/detailedposting/actions";
import { hasInvisibleComments, isCommentsShowInvisible } from "state/detailedposting/selectors";
import { openSheriffOrderDialog, sheriffOrderDelete } from "state/sherifforderdialog/actions";
import { recommendationDont } from "state/feeds/actions";
import { useDispatcher } from "ui/hook";
import { MinimalStoryInfo } from "ui/types";
import { DropdownMenu, DropdownMenuItems } from "ui/control";
import { REL_CURRENT, REL_HOME } from "util/rel-node-name";
import { formatFullName } from "util/names";
import { ut } from "util/url";
import "ui/entry/EntryMenu.css";

interface Props {
    posting: PostingInfo;
    feedName: string | null;
    story: MinimalStoryInfo | null;
    detailed?: boolean;
}

function PostingMenuItems({posting, feedName, story, detailed}: Props) {
    const homeOwnerName = useSelector(getHomeOwnerName);
    const commentsSubscriptionId = useSelector((state: ClientState) =>
        getPostingCommentsSubscriptionId(state, posting.id, REL_CURRENT)
    );
    const commentAddedInstantBlockId = useSelector((state: ClientState) =>
        getPostingCommentAddedInstantBlockId(state, posting.id, REL_CURRENT)
    );
    const containsInvisibleComments = useSelector((state: ClientState) =>
        (detailed ?? false) && hasInvisibleComments(state)
    );
    const showInvisibleComments = useSelector((state: ClientState) =>
        (detailed ?? false) && isCommentsShowInvisible(state)
    );
    const postingEditable = useSelector((state: ClientState) => isPermitted("edit", posting, "owner", state));
    const postingDeletable = useSelector((state: ClientState) => isPermitted("delete", posting, "private", state));
    const confirmDelete = useSelector((state: ClientState) => getSetting(state, "posting.delete.confirm") as boolean);
    const storyEditable = useSelector((state: ClientState) =>
        story != null && isPermitted("edit", story, "admin", state)
    );
    const googlePlayGoverned = isPostingSheriff(posting, SHERIFF_GOOGLE_PLAY_TIMELINE);
    const googlePlaySheriff = useSelector((state: ClientState) =>
        getHomeOwnerName(state) === SHERIFF_GOOGLE_PLAY_TIMELINE
    );
    const googlePlayProhibited = isPostingSheriffProhibited(posting, SHERIFF_GOOGLE_PLAY_TIMELINE);
    const dispatch = useDispatcher();
    const {t} = useTranslation();

    const onCopyLink = () => dispatch(postingCopyLink(posting.id, REL_CURRENT));

    const onCopyText = () =>
        dispatch(entryCopyText(posting.body, "ask", posting.receiverName ?? REL_CURRENT, posting.media ?? null));

    const ownerName = posting.receiverName ?? posting.ownerName;
    const fullName = (posting.receiverName != null ? posting.receiverFullName : posting.ownerFullName) ?? null;
    const gender = (posting.receiverName != null ? posting.receiverGender : posting.ownerGender) ?? null;
    const postingId = posting.receiverPostingId ?? posting.id;

    const ownPosting = ownerName === homeOwnerName;
    const followingComments = ownPosting ? commentAddedInstantBlockId == null : commentsSubscriptionId != null;
    const originalDeleted = posting.receiverDeletedAt != null;

    const onReply = () => dispatch(postingReply(posting.id));

    const onFollowComments = () => {
        if (ownPosting) {
            dispatch(postingCommentAddedUnblock(posting.id, REL_CURRENT));
        } else {
            dispatch(postingCommentsSubscribe(posting.id, REL_CURRENT));
        }
    }

    const onUnfollowComments = () => {
        if (ownPosting) {
            dispatch(postingCommentAddedBlock(posting.id, REL_CURRENT));
        } else {
            dispatch(postingCommentsUnsubscribe(posting.id, REL_CURRENT));
        }
    }

    const onDelete = () => {
        const repostsLeft = (posting.feedReferences?.filter(r => r.feedName === "timeline") ?? []).length
            - (feedName == null || feedName === "timeline" ? 1 : 0);

        const onDelete = (deleteAll: boolean) =>
            feedName == null || story == null || repostsLeft <= 0 || deleteAll
                ? postingDelete(posting.id, REL_CURRENT)
                : storyDelete(REL_CURRENT, story, feedName, posting.id);

        if (confirmDelete) {
            dispatch(confirmBox({
                message: feedName == null && repostsLeft > 0
                    ? t("delete-post-with-reposts", {heading: posting.heading, count: repostsLeft})
                    : t("delete-post", {heading: posting.heading}),
                yes: t("delete"),
                no: t("cancel"),
                dontShowAgain: feedName != null && repostsLeft > 0
                    ? t("delete-more-reposts", {count: repostsLeft})
                    : undefined,
                dontShowAgainBox: feedName != null && repostsLeft > 0,
                onYes: onDelete,
                variant: "danger"
            }));
        } else {
            dispatch(onDelete(repostsLeft <= 0));
        }
    };

    const onPin = () => story != null && dispatch(storyPinningUpdate(story.id, !story.pinned));

    const onChangeDate = () => story != null && dispatch(openChangeDateDialog(story.id, story.publishedAt));

    const onDontRecommend = () => {
        dispatch(confirmBox({
            message: t(
                "dont-recommend-user",
                {name: formatFullName(posting.receiverName, posting.receiverFullName)}
            ),
            yes: t("dont-recommend"),
            no: t("cancel"),
            onYes: deleteAll => posting.receiverName ? recommendationDont(posting.receiverName, deleteAll) : null,
            variant: "danger",
            dontShowAgain: t("delete-all-his-recommended", {gender: posting.receiverGender}),
            dontShowAgainBox: true
        }));
    }

    const onViewSource = () => {
        if (posting.receiverName == null) {
            dispatch(openSourceDialog(REL_CURRENT, posting.id));
        } else {
            if (posting.receiverPostingId != null) {
                dispatch(openSourceDialog(posting.receiverName, posting.receiverPostingId));
            }
        }
    };

    const onShowInvisibleComments = () => dispatch(commentsShowInvisibleSet(true));

    const onHideInvisibleComments = () => dispatch(commentsShowInvisibleSet(false));

    const onHideInGooglePlay = () =>
        dispatch(openSheriffOrderDialog({
            nodeName: ownerName,
            fullName,
            feedName: "timeline",
            postingOwnerName: ownerName,
            postingOwnerFullName: fullName,
            postingOwnerGender: gender,
            postingId,
            postingHeading: posting.heading
        }));

    const onUnhideInGooglePlay = () => {
        dispatch(confirmBox({
            message: t("unhide-post-google-play", {heading: posting.heading}),
            yes: t("unhide"),
            no: t("cancel"),
            onYes: sheriffOrderDelete({nodeName: ownerName, feedName: "timeline", postingId}),
            variant: "success"
        }));
    };

    const postingHref = ut`/post/${posting.id}`;
    return (
        <DropdownMenuItems items={[
            {
                title: t("copy-link"),
                nodeName: REL_CURRENT,
                href: postingHref,
                onClick: onCopyLink,
                show: !originalDeleted
            },
            {
                title: t("copy-text"),
                nodeName: REL_CURRENT,
                href: postingHref,
                onClick: onCopyText,
                show: true
            },
            {
                title: t("reply"),
                nodeName: REL_HOME,
                href: "/compose",
                onClick: onReply,
                show: homeOwnerName != null && !originalDeleted
            },
            {
                title: t("follow-comments"),
                nodeName: REL_CURRENT,
                href: postingHref,
                onClick: onFollowComments,
                show: homeOwnerName != null && !followingComments && !originalDeleted
            },
            {
                title: t("unfollow-comments"),
                nodeName: REL_CURRENT,
                href: postingHref,
                onClick: onUnfollowComments,
                show: followingComments && !originalDeleted
            },
            {
                divider: true
            },
            {
                title: t("edit"),
                nodeName: REL_HOME,
                href: ut`/compose?id=${postingId}`,
                show: postingEditable
            },
            {
                title: story != null && !story.pinned ? t("pin") : t("unpin"),
                nodeName: REL_CURRENT,
                href: postingHref,
                onClick: onPin,
                show: storyEditable
            },
            {
                title: t("change-date-time"),
                nodeName: REL_CURRENT,
                href: postingHref,
                onClick: onChangeDate,
                show: (posting.receiverName == null || feedName === "timeline") && storyEditable
            },
            {
                divider: true
            },
            {
                title: t("dont-recommend"),
                nodeName: REL_CURRENT,
                href: postingHref,
                onClick: onDontRecommend,
                show: posting.recommended ?? false
            },
            {
                title: t("tune-recommendations"),
                nodeName: REL_HOME,
                href: "/settings/node#news",
                show: posting.recommended ?? false
            },
            {
                divider: true
            },
            {
                title: t("view-source"),
                nodeName: REL_CURRENT,
                href: postingHref,
                onClick: onViewSource,
                show: !originalDeleted
            },
            {
                title: t("delete"),
                nodeName: REL_CURRENT,
                href: postingHref,
                onClick: onDelete,
                show: postingDeletable
            },
            {
                divider: true
            },
            {
                title: t("show-hidden-comments"),
                nodeName: REL_CURRENT,
                href: postingHref,
                onClick: onShowInvisibleComments,
                show: containsInvisibleComments && !showInvisibleComments
            },
            {
                title: t("hide-hidden-comments"),
                nodeName: REL_CURRENT,
                href: postingHref,
                onClick: onHideInvisibleComments,
                show: containsInvisibleComments && showInvisibleComments
            },
            {
                divider: true
            },
            {
                title: t("hide-in-google-play"),
                nodeName: REL_CURRENT,
                href: postingHref,
                onClick: onHideInGooglePlay,
                show: googlePlaySheriff && (!googlePlayProhibited || !googlePlayGoverned)
            },
            {
                title: t("unhide-in-google-play"),
                nodeName: REL_CURRENT,
                href: postingHref,
                onClick: onUnhideInGooglePlay,
                show: googlePlaySheriff && (googlePlayProhibited || googlePlayGoverned)
            },
            {
                title: t("report-sheriff"),
                nodeName: REL_CURRENT,
                href: postingHref,
                onClick: onHideInGooglePlay,
                show: homeOwnerName != null && !googlePlaySheriff && !ownPosting
            }
        ]}/>
    );
}

const PostingMenu = ({posting, feedName, story, detailed}: Props) =>
    <DropdownMenu content={
        <PostingMenuItems posting={posting} feedName={feedName} story={story} detailed={detailed}/>
    } className="posting-menu"/>;

export default PostingMenu;
